export const blogPosts = [
  {
    slug: 'the-silent-drop',
    title: 'The Silent Drop',
    subtitle: 'Finding what you can\'t see in a Kafka pipeline',
    date: 'June 2025',
    readTime: '6 min read',
    tags: ['Kafka', 'Debugging', 'Distributed Systems'],
    excerpt:
      'Orders were not propagating across five countries. No errors in the logs. No alerts firing. Just silence where messages should have been. This is the story of tracing a failure you cannot see.',
    content: [
      {
        type: 'p',
        text: 'At Jumia, the fulfilment platform processes over ten thousand transactions a day across five countries. During promotional campaigns that number triples. The system is loud by design — events, acknowledgements, status changes flowing constantly through Kafka topics. So when things go quiet, you notice.',
      },
      {
        type: 'p',
        text: 'Except we did not notice. Not right away. That is the thing about silent failures — they do not announce themselves.',
      },
      {
        type: 'h2',
        text: 'What silence looks like in a distributed system',
      },
      {
        type: 'p',
        text: 'The symptom was deceptively simple: orders were not propagating to downstream systems. No errors in the application logs. No consumer lag alerts. No dead letter queue accumulation. The Kafka brokers looked healthy. The producers were confirming acknowledgements. On every dashboard we owned, everything was green.',
      },
      {
        type: 'quote',
        text: 'Fixing silent failures is harder than fixing loud ones — you have to find what you can\'t see.',
      },
      {
        type: 'p',
        text: 'The first instinct when a distributed system misbehaves is to look at what is there — error logs, exception traces, metric spikes. Silent failures require inverting that instinct: look at what is not there. Which messages should exist that do not? Which state transitions should have happened? Which downstream records are absent?',
      },
      {
        type: 'h2',
        text: 'Tracing the gap',
      },
      {
        type: 'p',
        text: 'We started at the consumer side and worked backwards. Messages were being consumed — the offset was advancing. But the business logic that should have triggered on those messages was not executing. The consumers were receiving messages and silently doing nothing.',
      },
      {
        type: 'p',
        text: 'One step deeper: the consumers were calling an internal secrets vault to retrieve credentials before processing each message. Under normal load, this worked fine. Under the concurrency spike of a promotion, dozens of consumers were hitting the vault simultaneously. The vault was not failing — it was returning responses slowly. The consumers, not configured with a proper timeout, were hanging. The messages were being consumed off the queue but never processed.',
      },
      {
        type: 'stat',
        value: '−60%',
        label: 'reduction in cross-system sync failures after the fix',
      },
      {
        type: 'h2',
        text: 'The fix — and what it taught me',
      },
      {
        type: 'p',
        text: 'The immediate fix was adding aggressive timeouts and back-pressure on the vault client, along with a short-lived in-process cache for credentials that did not change between messages. Consumer throughput recovered within minutes of the deploy.',
      },
      {
        type: 'p',
        text: 'The structural fix was separating credential fetching from the hot path entirely — resolving secrets at consumer startup, refreshing them on a background schedule, never in the message-processing loop.',
      },
      {
        type: 'p',
        text: 'But the real lesson was about observability architecture. A system that fails loudly — with exceptions and stack traces — is a system you can debug. A system that fails silently requires a different class of instrumentation: tracking what did not happen, not just what did. Every consumer we owned after this got a "messages processed vs messages received" counter. Divergence became an alert, not a mystery.',
      },
    ],
  },
  {
    slug: 'atomic-double-allocation',
    title: 'Making Double Allocation Structurally Impossible',
    subtitle: 'Why the Kopo matching engine is a guarantee, not a hope',
    date: 'May 2025',
    readTime: '8 min read',
    tags: ['Fintech', 'Concurrency', 'Architecture'],
    excerpt:
      'Two lenders. One borrower. One millisecond. The matching logic was the straightforward part. Making it physically impossible for the same capital to be allocated twice — that was the real engineering problem.',
    content: [
      {
        type: 'p',
        text: 'Kopo is a peer-to-peer lending platform. Borrowers list their funding needs. Lenders browse profiles and choose where to deploy capital. The matching engine pairs them — balancing collateral types, interest rate expectations, and risk profiles simultaneously using a Bayesian scoring approach.',
      },
      {
        type: 'p',
        text: 'Getting to a 90% match success rate on a system handling real capital allocation decisions was the product goal. But there was a harder engineering problem sitting underneath the matching logic itself.',
      },
      {
        type: 'h2',
        text: 'The concurrency problem nobody wants to think about',
      },
      {
        type: 'p',
        text: 'Imagine two lenders both viewing the same borrower profile at the same moment. Both decide to fund them. Both click "Fund" within the same millisecond. Without careful design, both transactions can read the same borrower status — "unfunded" — before either write has committed. Both proceed. The same borrower receives twice the capital they asked for. Real money. Real problem.',
      },
      {
        type: 'quote',
        text: 'The hard problem was not the matching logic. It was making double-allocation physically impossible when two lenders hit the same borrower at the same millisecond.',
      },
      {
        type: 'p',
        text: 'The naive solution is optimistic locking — read the record with a version number, write only if the version has not changed, retry on conflict. It works. But on a high-concurrency lending platform during peak hours, retry storms are a real risk. And the worst outcome — a double allocation that sneaks through during a retry window — is not recoverable gracefully. Capital is not data you roll back casually.',
      },
      {
        type: 'h2',
        text: 'Atomic writes across the full transaction',
      },
      {
        type: 'p',
        text: 'The solution was designing the entire matching transaction as a single atomic write operation. The lender selection, the borrower status update, the loan record creation, and the ledger entries all commit together or none of them do. There is no state in which the lender is marked as allocated but the borrower is still available, even transiently.',
      },
      {
        type: 'p',
        text: 'This is not a novel idea — it is database transactions as they were designed to be used. The insight was recognising that the matching logic should live inside the transaction boundary, not outside it. The Bayesian scoring runs first, produces a match candidate, and then a single database transaction atomically claims the allocation or aborts. No retries needed at the application layer because the database is doing exactly what it is designed to do.',
      },
      {
        type: 'stat',
        value: '90%',
        label: 'match success rate — with zero double allocations in production',
      },
      {
        type: 'h2',
        text: 'Structural guarantees vs. defensive programming',
      },
      {
        type: 'p',
        text: 'There is a meaningful difference between a system that tries hard not to double-allocate and a system where double allocation is structurally impossible. The first relies on every code path being correct, every race condition being anticipated, every retry being safe. The second relies on the database\'s isolation guarantees — which have been proven correct for decades.',
      },
      {
        type: 'p',
        text: 'When you are handling people\'s capital, you want structural guarantees. Not hopes.',
      },
    ],
  },
  {
    slug: 'strategy-pattern-ocr',
    title: 'Why I Rebuilt the OCR Layer with a Strategy Pattern',
    subtitle: 'Untangling regional logic from a reconciliation engine',
    date: 'April 2025',
    readTime: '7 min read',
    tags: ['Design Patterns', 'Architecture', 'Reconciliation'],
    excerpt:
      'Region-specific OCR logic was scattered across a reconciliation codebase covering India, China, and the Philippines. Low-confidence extractions were reaching the reconciliation engine silently. One refactor fixed both problems.',
    content: [
      {
        type: 'p',
        text: 'Standard Focus processes bank transfer receipts across three countries — India, China, and the Philippines. Each country has its own receipt format, its own date conventions, its own currency representation. The OCR engine that extracts structured data from these receipts had grown organically, and the result was what you would expect: region-specific logic scattered throughout the codebase, interleaved with the reconciliation logic that consumed it.',
      },
      {
        type: 'h2',
        text: 'Two problems, one root cause',
      },
      {
        type: 'p',
        text: 'The first problem was structural. Adding a new region — say, Vietnam — required touching code throughout the OCR layer, the validation layer, and the reconciliation engine itself. The boundaries between "what region is this?" and "how do I parse this?" and "how do I reconcile this?" were blurred. Every change was risky.',
      },
      {
        type: 'p',
        text: 'The second problem was more subtle and more dangerous: low-confidence OCR extractions were reaching the reconciliation engine without any signal that confidence was low. A receipt where the amount field was partially obscured would produce an extracted value, the confidence score would be logged somewhere, and the reconciliation engine would proceed as if the value were reliable. Matches would succeed or fail for reasons that had nothing to do with the underlying transaction.',
      },
      {
        type: 'quote',
        text: 'Low-confidence extractions were reaching reconciliation silently. The engine had no knowledge that what it was working with was uncertain.',
      },
      {
        type: 'h2',
        text: 'The Strategy Pattern as an architecture, not a pattern',
      },
      {
        type: 'p',
        text: 'The fix was straightforward once the problem was framed correctly. The OCR layer needed a single entry point that routed each receipt to the correct regional processor, and every regional processor needed to return the same normalised schema regardless of the source format.',
      },
      {
        type: 'p',
        text: 'The metadata classifier became the routing brain. It reads the receipt header — sender bank, SWIFT code, formatting conventions — and selects the appropriate strategy. The India processor, the China processor, and the Philippines processor each implement the same interface: they receive a raw receipt and return Amount, Date, Reference, Status, and a confidence score. The reconciliation engine receives only that normalised schema. It has no knowledge of which processor ran, which region the receipt came from, or what the raw input looked like.',
      },
      {
        type: 'stat',
        value: '+30%',
        label: 'improvement in OCR confidence scores before data touched reconciliation',
      },
      {
        type: 'h2',
        text: 'What changed',
      },
      {
        type: 'p',
        text: 'The confidence score became a first-class field in the normalised schema, not a log entry. The reconciliation engine could now inspect it and route low-confidence extractions to a manual review queue instead of attempting automated matching. The failure mode changed from silent incorrect match to explicit human review.',
      },
      {
        type: 'p',
        text: 'Adding a new region became a new class implementing the processor interface, plus a new routing rule in the classifier. The reconciliation engine requires zero changes. That is the goal of a good abstraction — not cleverness, but the ability to extend the system without touching what already works.',
      },
    ],
  },
];
